import React, { useState, useEffect, useRef, useCallback } from 'react';
import NikeAISearch from '../NikeAISearch';
import { PanelWrapper } from './styles';
import useFeatureFlags from '../../utils/hooks/featureFlagsHook';
import { NIKE_AI } from '../../constants';

const EVENT_OPEN = 'nike-ai-search:open';
const EVENT_CLOSE = 'nike-ai-search:close';
const EVENT_TOGGLE = 'nike-ai-search:toggle';

const FOCUS_DELAY_MS = 150;

/**
 * Nike AI Panel Wrapper
 * Wraps the NikeAISearch remote component with focus management and ARIA support.
 *
 * The MFE handles all its own styling internally including:
 * - Desktop positioning, dimensions, and animations
 * - Mobile bottom sheet behavior
 * - Responsive breakpoints
 *
 * This wrapper provides:
 * - Z-index override to ensure panel appears above nav elements
 * - Focus management: moves focus into the panel on open, restores focus on close (WCAG 2.4.3)
 * - ARIA dialog attributes when the panel is open
 */
const NikeAIPanelWrapper = () => {
  const nikeAiEnabled = useFeatureFlags(NIKE_AI);
  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef(null);
  const triggerElementRef = useRef(null);

  const moveFocusToPanel = useCallback(() => {
    const panel = panelRef.current;
    if (!panel) return;

    const focusable = panel.querySelector(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    if (focusable) {
      focusable.focus();
    } else {
      panel.focus();
    }
  }, []);

  const restoreFocusToTrigger = useCallback(() => {
    const trigger = triggerElementRef.current;
    if (trigger && typeof trigger.focus === 'function') {
      trigger.focus();
    }
    triggerElementRef.current = null;
  }, []);

  useEffect(() => {
    const handleOpen = () => {
      setIsOpen(true);
    };

    const handleClose = event => {
      const { triggerElement } = event.detail || {};
      if (triggerElement) {
        triggerElementRef.current = triggerElement;
      }
      setIsOpen(false);
    };

    const handleToggle = event => {
      const { isOpen: newIsOpen, triggerElement } = event.detail || {};
      if (triggerElement) {
        triggerElementRef.current = triggerElement;
      }
      setIsOpen(newIsOpen);
    };

    window.addEventListener(EVENT_OPEN, handleOpen);
    window.addEventListener(EVENT_CLOSE, handleClose);
    window.addEventListener(EVENT_TOGGLE, handleToggle);

    return () => {
      window.removeEventListener(EVENT_OPEN, handleOpen);
      window.removeEventListener(EVENT_CLOSE, handleClose);
      window.removeEventListener(EVENT_TOGGLE, handleToggle);
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(moveFocusToPanel, FOCUS_DELAY_MS);
      return () => clearTimeout(timer);
    }
    restoreFocusToTrigger();
  }, [isOpen, moveFocusToPanel, restoreFocusToTrigger]);

  if (!nikeAiEnabled) {
    return null;
  }

  return (
    <PanelWrapper
      aria-label="Nike AI"
      aria-modal={isOpen ? 'true' : undefined}
      ref={panelRef}
      role={isOpen ? 'dialog' : undefined}
      tabIndex={-1}
    >
      <NikeAISearch />
    </PanelWrapper>
  );
};

NikeAIPanelWrapper.displayName = 'NikeAIPanelWrapper';

export default NikeAIPanelWrapper;
